import dynamic from 'next/dynamic'
import classnames from 'classnames'
import { ErrorMessage } from 'formik'
import { captureException } from 'marketing-site/lib/sentry'
import { Color } from 'marketing-site/src/library/utils'
import React, { ComponentType, ReactNode } from 'react'
import useTranslate from '../../hooks/useTranslate'
import { FieldType, IFormField, IHtmlTextField, isHiddenField } from './@types/FormField'
import appendPunctuationMark from './utils/appendPunctuationMark'
import { IFormFieldProps as IGenericFormFieldProps } from './Field.interface'
import { Email } from './fields/Email'

export type IFormFieldProps = IFormField & {
  hasError: boolean
  customErrorMessage?: string
  enableValidation: boolean
  isDisabled: boolean
  onChange: (e: React.ChangeEvent<any>) => void
  localeCode: string
}

export const sanitizeText = (text: string) => text.replace(/(<([^>]+)>)/gi, '')

export const testIds = {
  errorMessage: 'form-field-error-bubble',
}

function localiseAndRenderField(field: IFormFieldProps, translate: (text: string) => string) {
  const translatedField = { ...field }
  // props to translate are label and placeholder
  if ('label' in translatedField && translatedField.label) {
    translatedField.label = localiseFieldText(translatedField.label, translate)
  }

  if ('placeholder' in translatedField && translatedField.placeholder) {
    translatedField.placeholder = localiseFieldText(translatedField.placeholder, translate)
  }

  return renderField(translatedField)
}

function localiseFieldText(fieldText: string, translate: (text: string) => string) {
  const translatedText = translate(fieldText)
  const translatedTextWithPunctuation = appendPunctuationMark(fieldText, translatedText)
  return translatedTextWithPunctuation
}

const renderField = (field: IFormFieldProps): ReactNode => {
  const FieldComponent = FieldComponents[field.type]

  if (!FieldComponent) {
    const unknownField = field as any
    captureException(new Error(`Attempted to render unknown form field type=${unknownField?.type}`))
    return null
  }

  return <FieldComponent {...field} />
}

// TODO: fix types mapping to avoid any
const FieldComponents: Record<FieldType, ComponentType<IGenericFormFieldProps<any>>> = {
  [FieldType.TEXT]: dynamic(async () => (await import('./fields/Input')).Input),
  [FieldType.HTML_TEXT]: (props: IHtmlTextField) => (
    <div dangerouslySetInnerHTML={{ __html: props.text || '' }} />
  ),
  [FieldType.CHECKBOX]: dynamic(async () => (await import('./fields/Checkbox')).Checkbox),
  [FieldType.RADIO_BUTTONS]: dynamic(
    async () => (await import('./fields/RadioButtons')).RadioButtons,
  ),
  [FieldType.SELECT]: dynamic(async () => (await import('./fields/Select/Select')).Select),
  [FieldType.TEXT_AREA]: dynamic(async () => (await import('./fields/Textarea')).Textarea),
  // Not using dynamic import for email, in order to prevent form from being loaded without fields
  [FieldType.EMAIL]: Email,
  [FieldType.HIDDEN]: dynamic(async () => (await import('./fields/Hidden')).Hidden),
  [FieldType.NUMBER]: dynamic(async () => (await import('./fields/Number')).Number),
  [FieldType.PHONE]: dynamic(async () => (await import('./fields/Phone')).PhoneField),
}

const FormField = React.memo((field: IFormFieldProps) => {
  const { id, hasError, enableValidation, customErrorMessage, type } = field
  const { translate } = useTranslate()

  const wrapperClassNames = classnames('form-field', {
    'form-field-visible': !isHiddenField(field),
    'form-field-has-error': enableValidation && hasError,
  })

  const formDirection = type === 'checkbox' ? 'row' : 'column'

  return (
    <>
      <div className={wrapperClassNames} key={id}>
        {localiseAndRenderField(field, translate)}

        {/* This is populated by Formik if the field with the same name has errors */}
        {enableValidation && (
          <ErrorMessage
            name={id}
            className="error-message"
            component="div"
            data-testid={testIds.errorMessage}
            render={(errorMsg) => (
              <div className="error-message" data-testid={testIds.errorMessage}>
                {customErrorMessage || errorMsg}
              </div>
            )}
          />
        )}
      </div>
      <style jsx>{`
        .form-field {
          display: none;

          &.form-field-visible {
            display: flex;
            flex-direction: ${formDirection};
            column-gap: 0.75rem;
            margin-bottom: 1em;
          }

          &.form-field-has-error {
            margin-bottom: 0;

            :global(.error-message) {
              position: relative;
              display: block;
              text-align: center;
              background-color: ${Color.UIError};
              color: ${Color.White};
              border-radius: 30px;
              padding: 8px;
              margin: 12.5px auto 2.5px;

              &:before {
                position: absolute;
                display: block;
                bottom: 100%;
                left: 50%;
                content: ' ';
                height: 10px;
                width: 10px;
                background-color: ${Color.UIError};
                border-radius: 2px;
                transform: translate(-50%, 0) rotate(45deg);
                top: -4px;
              }
            }
          }
        }
      `}</style>
    </>
  )
})

export default FormField
